/** Non-secret identity persistence for optimistic session resume. */
import { anonymousState, type AuthState } from './reducer.ts';

export const STORAGE_KEY = '1nl.auditor.session.v1';
export const LEGACY_STORAGE_KEY = '1nl.auditor.auth.v1';

interface PersistedShape {
  email: unknown;
  displayName: unknown;
  userId: unknown;
  roleName: unknown;
  permissions: unknown;
}

/** Write the authenticated session; anonymous state clears the key instead. */
export function persistAuth(state: AuthState): void {
  try {
    if (state.status !== 'authenticated') {
      localStorage.removeItem(STORAGE_KEY);
      return;
    }
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        email: state.email,
        displayName: state.displayName,
        userId: state.userId,
        roleName: state.roleName,
        permissions: state.permissions,
      }),
    );
  } catch {
    // Private-mode / quota errors must never crash login; session stays in memory.
  }
}

export function clearAuth(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

/** Persisted identity whose cookie-backed session must be refreshed on boot. */
export interface ResumableAuthSession {
  status: 'resumable';
  email: string;
  displayName: string | null;
  userId: number;
  roleName: string;
  permissions: string[];
}

export type LoadedAuth = AuthState | ResumableAuthSession;

/**
 * Load a previously persisted identity. Every valid identity is resumable;
 * the access token is never persisted and must be refreshed from the cookie.
 */
export function loadAuth(): LoadedAuth {
  let raw: string | null;
  try {
    localStorage.removeItem(LEGACY_STORAGE_KEY);
    raw = localStorage.getItem(STORAGE_KEY);
  } catch {
    return anonymousState;
  }
  if (!raw) return anonymousState;

  let parsed: PersistedShape;
  try {
    parsed = JSON.parse(raw) as PersistedShape;
  } catch {
    clearAuth();
    return anonymousState;
  }

  const { email, userId, roleName, permissions } = parsed;
  // A missing displayName remains valid and defaults to null.
  const displayName = parsed.displayName === undefined ? null : parsed.displayName;
  const valid =
    typeof email === 'string' &&
    (displayName === null || typeof displayName === 'string') &&
    typeof userId === 'number' &&
    typeof roleName === 'string' &&
    Array.isArray(permissions) &&
    permissions.every((p) => typeof p === 'string');

  if (!valid) {
    clearAuth();
    return anonymousState;
  }

  return {
    status: 'resumable',
    email,
    displayName,
    userId,
    roleName,
    permissions,
  };
}
